import type { File } from "@mux/mux-node/_shims/auto/types";
import {
  FaFileAudio,
  FaFileImage,
  FaFilePdf,
  FaFileZipper,
  FaFile,
} from "react-icons/fa6";
import { StudioFileStatus, StudioFileType } from "@prisma/client";
import { IconType } from "react-icons/lib/iconBase";

import { FullFile, FullPostFile } from "./types";
import { trpcBrowserClient } from "./trpc/browser";

export type UploadIntent = "post" | "resource";

export type ResourceTypeOption =
  | "DOCUMENT"
  | "AUDIO"
  | "VIDEO"
  | "IMAGE"
  | "LINK"
  | "OTHER";

export interface FileUploadOptions {
  maxFileSize?: number; // in bytes
  allowedMimeTypes?: string[];
  maxFiles?: number;
}

export const DEFAULT_FILE_UPLOAD_OPTIONS: FileUploadOptions = {
  maxFileSize: 1024 * 1024 * 1024, // 1GB
  maxFiles: 10,
  allowedMimeTypes: [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
    "audio/mpeg",
    "audio/wav",
    "application/pdf",
    "application/zip",
    "application/x-zip-compressed",
    "application/x-zip",
    "application/x-compressed",
    "multipart/x-zip",
    "application/x-rar-compressed",
    "application/vnd.ms-powerpoint",
    "application/vnd.ableton.live-set",
    "application/vnd.ableton.analysis",
    "application/vnd.ableton.analysis-data",
    "application/x-flstudio",
    "application/octet-stream",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/plain",
    "application/rtf",
    "application/vnd.oasis.opendocument.text",
    "application/vnd.oasis.opendocument.spreadsheet",
    "application/vnd.oasis.opendocument.presentation",
  ],
};

export function validateFile(
  file: File,
  options: FileUploadOptions = DEFAULT_FILE_UPLOAD_OPTIONS,
): { valid: boolean; error?: string } {
  // Check file size
  if (options.maxFileSize && file.size > options.maxFileSize) {
    return {
      valid: false,
      error: `File size exceeds maximum allowed size of ${formatFileSize(options.maxFileSize)}`,
    };
  }

  // Check MIME type
  if (
    options.allowedMimeTypes &&
    !options.allowedMimeTypes.includes(file.type)
  ) {
    return {
      valid: false,
      error: `File type ${file.type} is not allowed`,
    };
  }

  return { valid: true };
}

export function formatFileSize(bytes: number | bigint): string {
  const size = typeof bytes === "bigint" ? Number(bytes) : bytes;
  const units = ["B", "KB", "MB", "GB", "TB"];
  let unitIndex = 0;
  let fileSize = size;

  while (fileSize >= 1024 && unitIndex < units.length - 1) {
    fileSize /= 1024;
    unitIndex++;
  }

  return `${fileSize.toFixed(1)} ${units[unitIndex]}`;
}

export function getFileIcon(fileType: StudioFileType) {
  const icons: Record<StudioFileType, IconType> = {
    [StudioFileType.IMAGE]: FaFileImage,
    [StudioFileType.AUDIO]: FaFileAudio,
    [StudioFileType.DOCUMENT]: FaFilePdf,
    [StudioFileType.ARCHIVE]: FaFileZipper,
    [StudioFileType.OTHER]: FaFile,
  };

  return icons[fileType] || FaFile;
}

export function formatFileStatus(status: StudioFileStatus) {
  const statuses: Record<StudioFileStatus, string> = {
    [StudioFileStatus.PENDING]: "Pending",
    [StudioFileStatus.UPLOADED]: "Uploaded",
    [StudioFileStatus.PROCESSING]: "Processing",
    [StudioFileStatus.READY]: "Ready",
    [StudioFileStatus.FAILED]: "Failed",
    [StudioFileStatus.DELETED]: "Deleted",
  };

  return statuses[status] || "Unknown";
}

export function formatFileStatusColor(status: StudioFileStatus) {
  const statuses: Record<StudioFileStatus, string> = {
    [StudioFileStatus.PENDING]: "bg-yellow-100 text-yellow-800",
    [StudioFileStatus.UPLOADED]: "bg-green-100 text-green-800",
    [StudioFileStatus.PROCESSING]: "bg-blue-100 text-blue-800",
    [StudioFileStatus.READY]: "bg-green-100 text-green-800",
    [StudioFileStatus.FAILED]: "bg-red-100 text-red-800",
    [StudioFileStatus.DELETED]: "bg-gray-100 text-gray-800",
  };

  return statuses[status] || "bg-gray-100 text-gray-800";
}

// Interfaces for multipart upload
export interface ChunkUploadProgress {
  loaded: number;
  total: number;
  chunkIndex: number;
  totalChunks: number;
}

interface MultipartUploadOptions {
  studioId: string;
  postId?: string;
  onProgress?: (progress: number) => void;
  onChunkProgress?: (chunkProgress: ChunkUploadProgress) => void;
  chunkSize?: number;
  maxRetries?: number;
  useDirectUpload?: boolean; // Use presigned URLs (default: false)
  intent?: UploadIntent;
  resourceType?: ResourceTypeOption;
  signal?: AbortSignal;
}

// Constants
// Lower threshold to 4MB to avoid serverless function payload limits (~4.5MB)
// Files >= 4MB will use multipart upload to avoid hitting serverless payload limits
const LARGE_FILE_THRESHOLD = 4 * 1024 * 1024; // 4MB
// S3/R2 requires all parts except the last to be at least 5MB
// We'll handle Vercel's limit by using direct upload or smaller chunks via presigned URLs
const DEFAULT_CHUNK_SIZE = 5 * 1024 * 1024; // 5MB (S3 minimum)
const DEFAULT_MAX_RETRIES = 3;
// Minimum size for multipart uploads - files smaller than this use regular upload
// Lowered to 4MB to avoid serverless function payload limits
const MULTIPART_MIN_SIZE = 4 * 1024 * 1024; // 4MB
// Maximum parallel chunk uploads for faster large file uploads
const MAX_PARALLEL_CHUNKS = 3;

/**
 * Calculate optimal chunk size based on file size.
 * Larger files use bigger chunks to reduce total request count and improve reliability.
 * A 1GB file: 20MB chunks = ~51 chunks instead of 205 with 5MB chunks.
 */
function getOptimalChunkSize(fileSize: number): number {
  if (fileSize >= 500 * 1024 * 1024) return 20 * 1024 * 1024; // 20MB for 500MB+
  if (fileSize >= 100 * 1024 * 1024) return 10 * 1024 * 1024; // 10MB for 100-500MB
  return DEFAULT_CHUNK_SIZE; // 5MB for smaller files
}

// Generate unique upload ID
function generateUploadId(): string {
  return `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Sleep utility for retry delays
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Client-side multipart upload implementation
export async function uploadFilesMultipart(
  files: File[],
  options: MultipartUploadOptions,
): Promise<{
  success: boolean;
  files?: FullFile[];
  postFiles?: FullPostFile[];
  error?: string;
  extractionSummary?: any[];
}> {
  try {
    if (!options.studioId) {
      throw new Error("Studio ID is required");
    }

    const {
      studioId,
      postId,
      onProgress,
      onChunkProgress,
      chunkSize = DEFAULT_CHUNK_SIZE,
      maxRetries = DEFAULT_MAX_RETRIES,
      // Always use direct-to-R2 multipart uploads for parity across envs
      useDirectUpload = true,
      signal,
    } = options;
    // Mutable flag controlling path; default is direct everywhere
    let directUpload = useDirectUpload;

    const uploadedFiles: FullFile[] = [];
    const postFiles: FullPostFile[] = [];
    const extractionSummaries: any[] = [];
    let totalBytes = 0;
    let uploadedBytes = 0;

    // Calculate total bytes for overall progress
    files.forEach((file) => {
      totalBytes += file.size;
    });

    const uploadIntent = options.intent ?? "post";
    const resourceType = options.resourceType;

    for (const file of files) {
      let fileId: string | undefined;
      let r2Key: string | undefined;
      let multipartUploadId: string | undefined;

      try {
        // Create file record first
        const createPayload: Record<string, unknown> = {
          studioId,
          postId,
          filename: file.name,
          mimeType: file.type || "application/octet-stream",
          size: file.size,
          intent: uploadIntent,
        };

        if (resourceType) {
          createPayload.resourceType = resourceType;
        }

        let createResult: { fileId: string; r2Key: string };
        try {
          createResult = await trpcBrowserClient.files.create.mutate(
            createPayload as unknown as {
              filename: string;
              mimeType: string;
              size: number;
            },
          );
        } catch (error) {
          throw new Error(
            error instanceof Error && error.message
              ? error.message
              : "Failed to create file record",
          );
        }
        fileId = createResult.fileId;
        r2Key = createResult.r2Key;

        // Upload file chunks - use optimal chunk size for large files
        const effectiveChunkSize = options.chunkSize
          ? chunkSize
          : getOptimalChunkSize(file.size);
        const totalChunks = Math.ceil(file.size / effectiveChunkSize);
        const uploadId = generateUploadId();
        const completedParts: { ETag: string; PartNumber: number }[] = [];

        console.log(
          `Starting multipart upload for ${file.name}: ${totalChunks} chunks, chunk size: ${chunkSize}, file size: ${file.size}`,
        );

        // Initialize multipart upload
        let initResult: { multipartUploadId: string };
        try {
          initResult = await trpcBrowserClient.files.multipart.init.mutate({
            r2Key,
            mimeType: file.type || "application/octet-stream",
            uploadId,
            intent: uploadIntent,
            size: file.size,
            filename: file.name,
          });
        } catch (error) {
          throw new Error(
            error instanceof Error && error.message
              ? error.message
              : "Failed to initialize multipart upload",
          );
        }
        multipartUploadId = initResult.multipartUploadId;

        // Upload a single chunk with retry logic
        const uploadSingleChunk = async (
          chunkIndex: number,
        ): Promise<{ ETag: string; PartNumber: number }> => {
          // Check if upload was cancelled
          if (signal?.aborted) {
            throw new DOMException("Upload cancelled", "AbortError");
          }

          const start = chunkIndex * effectiveChunkSize;
          const end = Math.min(start + effectiveChunkSize, file.size);
          const chunk = file.slice(start, end);
          const partNumber = chunkIndex + 1;

          // Check if this is the last chunk and if it's too small
          // S3/R2 requires all parts except the last to be at least 5MB
          if (chunkIndex < totalChunks - 1 && chunk.size < 5 * 1024 * 1024) {
            throw new Error(
              `File size would result in chunks smaller than 5MB. Please use regular upload for files under ${LARGE_FILE_THRESHOLD / 1024 / 1024}MB.`,
            );
          }

          let uploadAttempts = 0;
          let chunkUploaded = false;

          while (uploadAttempts < maxRetries && !chunkUploaded) {
            try {
              let presignedUrl = "";

              if (directUpload) {
                // Get presigned URL for direct upload
                const result =
                  await trpcBrowserClient.files.multipart.presignPart.mutate(
                    {
                      r2Key: r2Key as string,
                      uploadId: multipartUploadId as string,
                      partNumber,
                    },
                    { signal },
                  );
                presignedUrl = result.presignedUrl;
              }

              // Upload chunk either directly to R2 or through our API
              let uploadResponse: Response;
              let etag: string | null = null;

              if (directUpload) {
                // Direct upload to R2 using presigned URL
                console.log(
                  `Uploading chunk ${partNumber} directly to presigned URL`,
                );

                try {
                  uploadResponse = await fetch(presignedUrl, {
                    method: "PUT",
                    body: chunk,
                    // Do not set explicit headers to avoid signed-header mismatches/CORS preflights
                    mode: "cors",
                    signal,
                  });

                  if (!uploadResponse.ok) {
                    const responseText = await uploadResponse.text();
                    console.error(
                      `Direct chunk upload failed - Status: ${uploadResponse.status}, Response: ${responseText}`,
                    );
                    throw new Error(
                      `Chunk upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`,
                    );
                  }

                  etag =
                    uploadResponse.headers.get("ETag") ||
                    uploadResponse.headers.get("etag") ||
                    uploadResponse.headers.get("x-amz-etag");
                } catch (fetchError) {
                  console.error(
                    `Direct upload error for chunk ${partNumber}:`,
                    fetchError,
                  );
                  if (
                    fetchError instanceof TypeError &&
                    fetchError.message === "Failed to fetch"
                  ) {
                    throw new Error(
                      `Network error uploading chunk ${partNumber}. This may be due to CORS restrictions.`,
                    );
                  }
                  throw fetchError;
                }
              } else {
                // Upload through our API to avoid CORS issues
                console.log(`Uploading chunk ${partNumber} through API`);

                uploadResponse = await fetch(
                  "/api/files/multipart/upload-chunk",
                  {
                    method: "POST",
                    body: chunk,
                    // @ts-ignore
                    headers: {
                      "x-r2-key": r2Key,
                      "x-upload-id": multipartUploadId,
                      "x-part-number": partNumber.toString(),
                      "Content-Type": file.type || "application/octet-stream",
                    },
                    signal,
                  },
                );

                if (!uploadResponse.ok) {
                  // If the platform rejects the request body (e.g., Vercel 4.5MB limit),
                  // flip to direct-to-R2 uploads and retry this chunk on next attempt.
                  if (uploadResponse.status === 413) {
                    console.info(
                      `Received 413 from API for chunk ${partNumber}. Switching to direct-to-R2 uploads for remaining chunks.`,
                    );
                    directUpload = true;
                  }
                  const responseText = await uploadResponse.text();
                  console.error(
                    `API chunk upload failed - Status: ${uploadResponse.status}, Response: ${responseText}`,
                  );
                  throw new Error(
                    `Chunk upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`,
                  );
                }

                const result = await uploadResponse.json();
                etag = result.ETag;
              }

              // Check if we have an ETag
              if (!etag) {
                // Log for debugging
                console.warn(`No ETag received for chunk ${partNumber}`);

                // Generate a placeholder ETag for R2 compatibility
                const placeholderEtag = `"chunk-${partNumber}-${Date.now()}"`;
                console.warn(`Using placeholder ETag: ${placeholderEtag}`);

                chunkUploaded = true;
                return {
                  ETag: placeholderEtag,
                  PartNumber: partNumber,
                };
              } else {
                chunkUploaded = true;
                return {
                  ETag: etag.replace(/"/g, ""), // Remove quotes from ETag
                  PartNumber: partNumber,
                };
              }
            } catch (error) {
              uploadAttempts++;
              console.warn(
                `Chunk ${partNumber} upload attempt ${uploadAttempts} failed:`,
                error,
              );

              if (uploadAttempts >= maxRetries) {
                const errorMessage =
                  error instanceof Error ? error.message : "Unknown error";
                console.error(
                  `Chunk ${partNumber} failed after ${maxRetries} attempts:`,
                  errorMessage,
                );
                throw new Error(
                  `Failed to upload chunk ${partNumber} after ${maxRetries} attempts: ${errorMessage}`,
                );
              }

              // Exponential backoff
              const delay = Math.min(
                1000 * Math.pow(2, uploadAttempts - 1),
                10000,
              );
              await sleep(delay);
            }
          }

          throw new Error(`Failed to upload chunk ${chunkIndex + 1}`);
        };

        // Upload chunks in parallel batches for faster throughput
        for (
          let batchStart = 0;
          batchStart < totalChunks;
          batchStart += MAX_PARALLEL_CHUNKS
        ) {
          // Check if upload was cancelled before each batch
          if (signal?.aborted) {
            if (r2Key && multipartUploadId) {
              await abortMultipartUpload(r2Key, multipartUploadId, fileId);
            }
            throw new DOMException("Upload cancelled", "AbortError");
          }

          const batchEnd = Math.min(
            batchStart + MAX_PARALLEL_CHUNKS,
            totalChunks,
          );
          const batchIndices = Array.from(
            { length: batchEnd - batchStart },
            (_, i) => batchStart + i,
          );

          // Upload batch of chunks in parallel
          const batchResults = await Promise.all(
            batchIndices.map(uploadSingleChunk),
          );
          completedParts.push(...batchResults);

          // Update progress after each batch completes
          const chunksCompleted = batchEnd;
          uploadedBytes = Math.min(
            chunksCompleted * effectiveChunkSize,
            file.size,
          );
          const overallProgress = Math.round(
            (uploadedBytes / totalBytes) * 100,
          );
          onProgress?.(overallProgress);

          onChunkProgress?.({
            loaded: effectiveChunkSize,
            total: effectiveChunkSize,
            chunkIndex: chunksCompleted - 1,
            totalChunks,
          });

          console.log(
            `Uploaded batch ${Math.ceil(batchEnd / MAX_PARALLEL_CHUNKS)}/${Math.ceil(totalChunks / MAX_PARALLEL_CHUNKS)} for ${file.name}`,
          );
        }

        // Complete multipart upload
        const result = await trpcBrowserClient.files.multipart.complete.mutate({
          uploadId: multipartUploadId as string,
          parts: completedParts.sort((a, b) => a.PartNumber - b.PartNumber),
          fileId: fileId as string,
        });

        if (result.file) {
          uploadedFiles.push(
            result.file as unknown as (typeof uploadedFiles)[number],
          );
        }

        if (result.postFile) {
          postFiles.push(
            result.postFile as unknown as (typeof postFiles)[number],
          );
        }

        // ZIP extraction: complete no longer self-triggers it server-side —
        // fire the REST process-zip route directly (same fire-and-forget
        // semantics the legacy route had).
        if (result.needsZipProcessing && result.r2Key) {
          void fetch("/api/files/multipart/process-zip", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ fileId, r2Key: result.r2Key, studioId }),
          }).catch((zipProcessError) => {
            console.error(
              "Failed to trigger async ZIP processing:",
              zipProcessError,
            );
          });
        }

        console.log(`Successfully uploaded ${file.name} via multipart upload`);
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);

        // Try to abort the multipart upload if it was initialized
        try {
          if (multipartUploadId && r2Key) {
            await abortMultipartUpload(r2Key, multipartUploadId, fileId);
          }
        } catch (abortError) {
          console.warn("Failed to abort multipart upload:", abortError);
        }

        throw error;
      }
    }

    return {
      success: true,
      files: uploadedFiles,
      postFiles: postFiles,
      extractionSummary:
        extractionSummaries.length > 0 ? extractionSummaries : undefined,
    };
  } catch (error) {
    console.error("Multipart upload error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Multipart upload failed",
    };
  }
}

// Abort multipart upload function
export async function abortMultipartUpload(
  r2Key: string,
  uploadId: string,
  fileId?: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    // r2Key is derived server-side from fileId; the param stays for caller
    // compatibility.
    await trpcBrowserClient.files.multipart.abort.mutate({ uploadId, fileId });

    return { success: true };
  } catch (error) {
    console.error("Abort multipart upload error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Failed to abort upload",
    };
  }
}

// Enhanced uploadFiles function with automatic multipart detection
export async function uploadFiles(
  studioId: string,
  files: File[],
  options?: {
    postId?: string;
    onProgress?: (progress: number) => void;
    onChunkProgress?: (chunkProgress: ChunkUploadProgress) => void;
    forceMultipart?: boolean;
    intent?: UploadIntent;
    resourceType?: ResourceTypeOption;
    signal?: AbortSignal;
  },
): Promise<{
  success: boolean;
  files?: FullFile[];
  postFiles?: FullPostFile[];
  error?: string;
  extractionSummary?: any;
}> {
  try {
    if (!studioId) {
      throw new Error("Studio ID is required");
    }

    // Check if any file exceeds the large file threshold or if multipart is forced
    // Use multipart for files >= 4MB to avoid serverless function payload limits (~4.5MB)
    // If any file is >= 4MB, use multipart for all files to avoid payload limit issues
    const hasLargeFiles = files.some(
      (file) => file.size >= LARGE_FILE_THRESHOLD,
    );
    const shouldUseMultipart = hasLargeFiles || options?.forceMultipart;

    if (shouldUseMultipart) {
      console.log("Using multipart upload for large files or forced multipart");
      return await uploadFilesMultipart(files, {
        studioId,
        postId: options?.postId,
        onProgress: options?.onProgress,
        onChunkProgress: options?.onChunkProgress,
        // Force direct uploads in all environments for parity
        useDirectUpload: true,
        intent: options?.intent,
        resourceType: options?.resourceType,
        signal: options?.signal,
      });
    }

    // Use traditional upload for smaller files
    console.log("Using traditional upload for small files");

    const formData = new FormData();

    formData.append("studioId", studioId);

    if (options?.postId) {
      formData.append("postId", options.postId);
    }

    const uploadIntent = options?.intent ?? "post";
    formData.append("intent", uploadIntent);

    if (options?.resourceType) {
      formData.append("resourceType", options.resourceType);
    }

    for (const file of files) {
      formData.append("files", file);
    }

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && options?.onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        options.onProgress(progress);
      }
    });

    // Check if any files are ZIP files before upload
    const hasZipFiles = files.some(
      (f) => f.name.toLowerCase().endsWith(".zip") || f.type.includes("zip"),
    );

    const response = (await new Promise((resolve, reject) => {
      xhr.open("POST", "/api/files/upload");

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          // Try to extract error message from response
          let errorMessage = `Upload failed with status ${xhr.status}`;
          try {
            const errorData = JSON.parse(xhr.responseText);
            if (errorData?.error) {
              errorMessage = errorData.error;
            }
          } catch {
            // If parsing fails, use default message
          }
          reject(new Error(errorMessage));
        }
      };

      xhr.onerror = () => reject(new Error("Upload failed"));
      xhr.send(formData);
    })) as {
      success: boolean;
      files: FullFile[];
      postFiles: FullPostFile[];
      extractionSummary?: any;
    };

    return {
      success: true,
      files: response.files,
      postFiles: response.postFiles,
      extractionSummary: response.extractionSummary,
    };
  } catch (error) {
    console.error("Upload error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Upload failed",
    };
  }
}
